import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import FaqAccordion from '../components/FaqAccordion';
import { imageUrl } from '../lib/images';

const HERO_SLIDES = [
  {
    image: '/_assets/scania-autotransporter-ugerloese.png',
    eyebrow: 'Vagtcentral 24/7 · Landsdækkende',
    title: 'Autotransport og autohjælp i hele Danmark',
    subtitle: 'Hos MC Transport er autotransport vores centrale opgave. Vi flytter nye, brugte, skadede og brændte biler for både private og virksomheder.',
  },
  {
    image: '/_assets/volvo-bjaergningsvogn-motorvej-assistance.png',
    eyebrow: 'Tungvogn · Bjærgning · Fritrækning',
    title: 'Professionel bjærgning med Scania R660 & Uni Man 4x4',
    subtitle: 'Når uheldet er ude, trækker vi busser, lastbiler og maskiner sikkert fri',
  },
  {
    image: '/_assets/volvo-autotransporter-flerbils-transport.jpg',
    eyebrow: 'Sikker transport · Ind- og udland',
    title: 'Effektiv transport af nye, brugte og skadede biler',
    subtitle: 'Skydeladsbiler, kranbiler og transportere op til 8 biler',
  },
  {
    image: '/_assets/scania-lastbil-blokvogn-transport.jpg',
    eyebrow: '28 tm Kranbil · Specialtransport',
    title: 'Krantransport og maskintransport til enhver opgave',
    subtitle: 'Entreprenørmateriel, containere, både og sengetrailer',
  }
];

const CORE_SERVICES = [
  {
    title: 'Autohjælp / Vejhjælp',
    badge: 'Akut 24/7',
    desc: 'Hurtig vejhjælp og assistance ved nedbrud, punktering, starthjælp og fejeblad i hele landet.',
    image: '/_assets/bjaergningsvogn-autohjaelp-havareret-lastbil.jpg',
    link: '/autohjaelp'
  },
  {
    title: 'Bjærgning / Bugsering',
    badge: 'Tungvogn & 4x4',
    desc: 'Professionel bjærgning og fritrækning af lastbiler, busser, entreprenørmaskiner og biler.',
    image: '/_assets/bjaergning-med-uni-man-og-scania-18-03-2025-232.jpg',
    link: '/bjaergning'
  },
  {
    title: 'Autotransport',
    badge: 'Op til 8-9 biler',
    desc: 'Sikker transport af nye, brugte, skadede og brændte biler for private og erhverv.',
    image: '/_assets/volvo-autotransporter-flerbils-transport.jpg',
    link: '/ydelser/autotransport-ydelser'
  },
  {
    title: 'Nødservice / Breakdown service',
    badge: 'Mobil servicevogn',
    desc: 'Rullende værksted med specialværktøj, slanger og trykluft til akut reparation og nødservice.',
    image: '/_assets/servicevogn-5-214.jpg',
    link: '/ydelser/servicevogn'
  },
  {
    title: 'Kran & Maskintransport',
    badge: '28 tm Kran',
    desc: 'Transport af entreprenørmateriel, generatorer, containere, skurvogne, både og sengetrailer.',
    image: '/_assets/mc-transport-kranbil-28-tm.jpg',
    link: '/ydelser/krantransport'
  },
  {
    title: 'TMA / Skiltevogn',
    badge: 'Vejsikkerhed',
    desc: 'Påkørselsdæmpere (TMA) og mobile skiltevogne til effektiv sikring af vejarbejde og ulykkessteder.',
    image: '/_assets/roed-volvo-og-2-lastbiler-18-03-2025-411.jpg',
    link: '/TMA'
  },
  {
    title: 'Tow Trucking all over Europe',
    badge: 'International',
    desc: 'Grænseoverskridende bjærgning, bugsering og biltransport i hele Europa med erfarne chauffører.',
    image: '/_assets/volvo-bjaergningslastbil-motorvej.jpg',
    link: '/tow-truck'
  },
  {
    title: 'Døgnbemandet vagtcentral',
    badge: 'Åben 24/7/365',
    desc: 'Personlig betjening døgnet rundt på 70 700 800. Vi rykker ud med det samme, når uheldet er ude.',
    image: '/_assets/martin-indehaver-mc-transport-kontor.jpg',
    link: '/kontakt',
    isHotline: true
  }
];

const FAQ_ITEMS = [
  {
    question: 'Hvad koster autotransport?',
    answer: 'En autotransport pris hos MC Transport beregnes ud fra afstand, antal køretøjer, bilens stand og behovet for kran eller særlig læsning. Derfor gives der et konkret tilbud, før transporten planlægges.'
  },
  {
    question: 'Kan man få autotransport i hele Danmark?',
    answer: 'Ja. MC Transport udfører autotransport hele Danmark rundt for både private og virksomheder. Virksomheden kan flytte én bil eller flere køretøjer med materiel, der tilpasses den konkrete opgave.'
  },
  {
    question: 'Hvilke typer biler kan transporteres?',
    answer: 'MC Transport tilbyder det, der ofte søges som MC autotransport: transport af nye, brugte, skadede og brændte biler. Både køreklare biler og køretøjer, der kræver særlig læsning, kan håndteres.'
  },
  {
    question: 'Hvornår har man brug for autohjælp, bugsering eller bjærgning?',
    answer: 'Autohjælp er relevant, når bilen ikke kan fortsætte. Hos MC Transport bruges bugsering typisk til at flytte bilen til et værksted eller en anden adresse, mens bjærgning eller fritrækning kan være nødvendig, hvis bilen er fastkørt eller svært tilgængelig. Vagtcentralen er åben døgnet rundt.'
  },
  {
    question: 'Hvordan transporteres en bil, der ikke kan køre?',
    answer: 'MC Transport vurderer bilens stand og adgangsforholdene og vælger egnet materiel, eksempelvis en skydeladsbil, kranbil eller et bjærgningskøretøj. Ved transport af flere biler kan der bruges en transporter med plads til flere køretøjer.'
  },
  {
    question: 'Kan et transportfirma også flytte maskiner, containere og både?',
    answer: 'Ja. MC Transport udfører maskintransport, krantransport og godstransport af blandt andet entreprenørmateriel, containere og både. Opgaven kan løses med sengetrailer eller gardintrailer med medbringertruck, når læsning og aflæsning kræver det.'
  },
  {
    question: 'Hvilke oplysninger skal man give ved bestilling af autotransport?',
    answer: 'MC Transport har brug for at vide, hvad der skal flyttes, hvor det skal hentes og leveres, og om køretøjet kan køre. Oplysningerne bruges til at vælge det rette materiel og planlægge læsning og levering.'
  },
  {
    question: 'Kan køretøjer og gods opbevares midlertidigt?',
    answer: 'Ja. MC Transport tilbyder midlertidig opbevaring af køretøjer og gods, når opbevaringen skal kombineres med transportopgaven.'
  }
];

const GALLERY_IMAGES = [
  {
    src: '/_assets/mc-autotransporter-med-skadede-biler.png',
    alt: 'Rød autotransporter med skadede biler',
    title: 'Autotransport af personbiler',
  },
  {
    src: '/_assets/volvo-fejeblad-det-roede-lyn.jpg',
    alt: 'Rød Volvo fejebladslastbil Det Røde Lyn',
    title: 'Det Røde Lyn · Hurtig udrykning',
  },
  {
    src: '/_assets/scania-lastbil-blokvogn-transport.jpg',
    alt: 'Rød Scania-lastbil med blokvogn og blå lastbil',
    title: 'Tungtransport på blokvogn',
  },
  {
    src: '/_assets/bjaergningsvogn-autohjaelp-havareret-lastbil.jpg',
    alt: 'Rød bjærgningsvogn yder vejhjælp til havareret lastbil',
    title: 'Akut bjærgning & vejhjælp',
  },
  {
    src: '/_assets/bjaergningsvogn-blokvogn-mc-transport.jpg',
    alt: 'Rød bjærgningsvogn med blokvogn gennem byområde',
    title: 'Specialtransport i by og land',
  },
  {
    src: '/_assets/volvo-autotransporter-flerbils-transport.jpg',
    alt: 'Rød Volvo autotransporter læsset med biler i to etager',
    title: 'Effektiv flerbils-transport',
  },
  {
    src: '/_assets/gul-bjaergningsvogn-mc-transport.jpg',
    alt: 'Gul bjærgningsvogn fra MC Transport',
    title: 'Tung bjærgning 24/7',
  },
  {
    src: '/_assets/roed-autotransporter-landevej.png',
    alt: 'Rød autotransporter på landevej med biler og traktor',
    title: 'Blandet vognlæs til landsdelen',
  },
  {
    src: '/_assets/daf-autotransporter-campingvogne.jpg',
    alt: 'Rød DAF autotransporter med campingvogne',
    title: 'Transport af campingvogne',
  },
  {
    src: '/_assets/scania-autotransporter-ugerloese.png',
    alt: 'Rød Scania autotransporter foran værksted',
    title: 'MC Transport i Ugerløse',
  }
];

export default function HomePage() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [isGalleryPaused, setIsGalleryPaused] = useState(false);

  // Auto-advance slides every 6s
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % HERO_SLIDES.length);
    }, 6500);
    return () => clearInterval(timer);
  }, []);

  // Autoplay 3 sec for Gallery carousel
  useEffect(() => {
    if (isGalleryPaused) return;
    const timer = setInterval(() => {
      setGalleryIndex((prev) => (prev + 1) % GALLERY_IMAGES.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [isGalleryPaused]);

  const handlePrevGallery = () => {
    setGalleryIndex((prev) => (prev - 1 + GALLERY_IMAGES.length) % GALLERY_IMAGES.length);
  };

  const handleNextGallery = () => {
    setGalleryIndex((prev) => (prev + 1) % GALLERY_IMAGES.length);
  };

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': 'https://mcautotransport.dk/#webpage',
        'url': 'https://mcautotransport.dk/',
        'name': 'Autotransport i hele Danmark | Få hjælp døgnet rundt',
        'description': 'Få professionel autotransport i hele Danmark hos MC Transport. Vi hjælper med autohjælp, bugsering og bjærgning døgnet rundt, få et tilbud til din opgave.',
        'inLanguage': 'da-DK'
      },
      {
        '@type': 'AutoRepair',
        '@id': 'https://mcautotransport.dk/#organization',
        'name': 'MC Transport',
        'telephone': '+45 70 700 800',
        'email': 'kontor@mcautotransport.dk',
        'address': {
          '@type': 'PostalAddress',
          'streetAddress': 'Annexgårdsvej 7',
          'postalCode': '4350',
          'addressLocality': 'Ugerløse',
          'addressCountry': 'DK'
        },
        'openingHours': 'Mo,Tu,We,Th,Fr,Sa,Su 00:00-24:00',
        'description': 'Autotransport og autohjælp i hele Danmark. Vagtcentral døgnet rundt 24/7.'
      }
    ]
  };

  return (
    <>
      <SEOHead
        title="Autotransport i hele Danmark | MC Transport"
        description="Få professionel autotransport i hele Danmark hos MC Transport. Vi hjælper med autohjælp, bugsering og bjærgning døgnet rundt, få et tilbud til din opgave."
        schemaData={schemaData}
      />

      {/* HØJT SYNLIG DØGNBEMANDET VAGTCENTRAL ANNONCERING */}
      <div className="bg-red-600 text-white py-2.5 px-4 text-center border-b border-red-700 shadow-sm relative z-30">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-center gap-2 sm:gap-4 text-xs sm:text-sm font-condensed font-bold uppercase tracking-wider">
          <span className="inline-flex items-center gap-2">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-white"></span>
            </span>
            <span>Døgnbemandet vagtcentral 24/7/365</span>
          </span>
          <span className="hidden sm:inline text-red-200">·</span>
          <span>Akut hjælp & transport i hele Danmark</span>
          <span className="hidden sm:inline text-red-200">·</span>
          <a
            href="tel:+4570700800"
            className="inline-flex items-center gap-1.5 underline decoration-white/60 hover:decoration-white hover:text-white transition-colors"
          >
            <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
            <span>Ring direkte: +45 70 700 800</span>
          </a>
        </div>
      </div>

      {/* 1. PRIMÆRT VISUELT FOKUS: 8 YDELSER OVERBLIK DIREKTE VED INDGANGEN */}
      <section className="bg-zinc-950 text-white pt-8 pb-14 sm:pb-18 lg:pb-20 border-b border-zinc-800 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header intro - tilpasset så de 8 ydelser er i fokus med det samme */}
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8 sm:mb-10">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-red-600/90 text-white text-[11px] font-bold uppercase tracking-wider mb-2.5">
                <i className="fa-solid fa-truck-fast text-xs" aria-hidden="true" />
                <span>Vores 8 primære ydelser</span>
              </div>
              <h1 className="font-condensed text-3xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight text-white leading-[1.05]">
                Hvad kan vi hjælpe dig med?
              </h1>
            </div>
            <div className="max-w-md">
              <p className="text-xs sm:text-sm text-zinc-300 leading-relaxed">
                Vælg den ønskede ydelse nedenfor for hurtig hjælp eller transport. Vores døgnbemandede vagtcentral sidder altid klar ved telefonerne.
              </p>
              <div className="mt-2.5 flex items-center gap-3 text-xs font-bold uppercase tracking-wider text-red-400 font-condensed">
                <a href="tel:+4570700800" className="hover:text-white transition-colors flex items-center gap-1.5">
                  <i className="fa-solid fa-phone" aria-hidden="true" />
                  <span>Vagtcentral: +45 70 700 800</span>
                </a>
              </div>
            </div>
          </div>

          {/* 8-Ydelser Responsiv Grid: Desktop 4x2, Tablet 2x4, Mobil 1 col */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {CORE_SERVICES.map((srv, idx) => (
              <Link
                key={idx}
                to={srv.link}
                className="group relative h-80 sm:h-88 lg:h-96 rounded-xl overflow-hidden shadow-lg border border-zinc-800 bg-zinc-900 flex flex-col justify-end p-5 sm:p-6 text-white transition-all transform hover:-translate-y-1.5 hover:shadow-2xl hover:border-red-600/60"
              >
                {/* Billede */}
                <img
                  src={imageUrl(srv.image, 'width=600,height=500,fit=cover,quality=80,format=auto')}
                  alt={`${srv.title} - MC Transport ydelse`}
                  className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500 brightness-[0.70]"
                />

                {/* Mørkt gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/55 to-transparent" />

                {/* Kort indhold */}
                <div className="relative z-10">
                  <div className="mb-2">
                    <span
                      className={`inline-block text-[10px] font-bold uppercase px-2 py-0.5 rounded tracking-wider ${
                        srv.badge.includes('24/7') || srv.badge.includes('Akut') || srv.badge.includes('24/7/365')
                          ? 'bg-red-600 text-white shadow'
                          : 'bg-zinc-800/90 text-zinc-200 border border-white/10'
                      }`}
                    >
                      {srv.badge}
                    </span>
                  </div>

                  <h2 className="font-condensed text-2xl sm:text-3xl font-bold uppercase tracking-tight text-white group-hover:text-red-400 transition-colors leading-tight">
                    {srv.title}
                  </h2>

                  <p className="text-xs sm:text-sm text-zinc-300 mt-2 line-clamp-2 leading-relaxed">
                    {srv.desc}
                  </p>

                  <div className="mt-4 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-red-400 group-hover:text-white transition-colors">
                    <span>{srv.isHotline ? 'Ring direkte / Kontakt' : 'Læs mere'}</span>
                    <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          <div className="mt-8 text-center flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/ydelser"
              className="inline-flex items-center gap-2 px-6 py-3 bg-zinc-900 hover:bg-zinc-800 text-white font-condensed text-base sm:text-lg font-bold uppercase rounded border border-zinc-700 transition-colors shadow"
            >
              <span>Se alle ydelser i oversigten</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
            <Link
              to="/bilopbevaring"
              className="inline-flex items-center gap-2 px-6 py-3 bg-zinc-900 hover:bg-zinc-800 text-white font-condensed text-base sm:text-lg font-bold uppercase rounded border border-zinc-700 transition-colors shadow"
            >
              <span>Bilopbevaring på sikret plads</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* 2. SEKUNDÆR BANNER: PROFESSIONEL BJÆRGNING, FRITRÆKNING & STOR AUTOTRANSPORTER */}
      <section className="relative w-full max-w-full bg-zinc-950 text-white py-16 sm:py-20 lg:py-24 border-b border-zinc-800 overflow-hidden">
        {/* Bannerbillede med stor autotransporter (9 biler) */}
        <img
          src={imageUrl('/_assets/blaa-volvo-med-9-biler-18-03-2025-359.jpg', 'width=1920,quality=85,format=auto')}
          alt="MC Transport autotransporter med biler og materiel til professionel bjærgning og fritrækning"
          className="absolute inset-0 w-full h-full object-cover object-center brightness-[0.40]"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-zinc-950/95 via-zinc-950/75 to-zinc-950/40" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-red-600/90 text-white text-xs font-bold uppercase tracking-wider mb-4 border border-red-500/50">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
              </span>
              <span>Døgnbemandet Vagtcentral · Landsdækkende</span>
            </div>

            <h2 className="font-condensed text-3xl sm:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight text-white leading-tight">
              Professionel bjærgning & fritrækning
            </h2>

            <p className="mt-4 text-base sm:text-lg text-zinc-200 leading-relaxed font-normal">
              Når uheldet er ude, rykker MC Transport ud med professionelt materiel til bjærgning og fritrækning af personbiler, busser, lastbiler og tungt materiel i hele Danmark. Vi råder desuden over store autotransportere til sikker flertransport af op til 8-9 biler ad gangen.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
              <a
                href="tel:+4570700800"
                className="inline-flex items-center justify-center gap-3 px-6 sm:px-8 py-3.5 bg-red-600 hover:bg-red-700 active:bg-red-800 text-white font-condensed text-xl font-bold uppercase tracking-wider rounded shadow-lg transition-all"
              >
                <i className="fa-solid fa-phone text-lg" aria-hidden="true" />
                <span>Ring 70 700 800 (Døgnvagt)</span>
              </a>
              <Link
                to="/bjaergning"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-zinc-900/90 hover:bg-zinc-800 text-white font-condensed text-xl font-bold uppercase tracking-wider rounded border border-white/20 transition-all text-center"
              >
                <span>Læs om bjærgning & fritrækning</span>
                <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 2. STORE LASTBIL-INSPIREREDE CTA-KNAPPER (Ring 70 700 800, Send Mail, Facebook) */}
      <section className="bg-zinc-900 text-white border-y border-zinc-800 py-8 px-4 sm:px-6 lg:px-8 relative z-20 -mt-1 shadow-2xl w-full max-w-full overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-6">
            <span className="text-xs font-bold uppercase tracking-widest text-red-500 font-condensed">
              Hurtig Kontakt · Direkte Adgang Døgnet Rundt
            </span>
            <h2 className="font-condensed text-2xl sm:text-3xl font-bold uppercase tracking-tight text-white mt-1">
              Tre nemme måder at fange os på
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6">
            {/* CTA 1: Telefon */}
            <a
              href="tel:+4570700800"
              className="group relative overflow-hidden bg-gradient-to-r from-red-700 to-red-600 hover:from-red-600 hover:to-red-500 text-white p-5 rounded-lg border-2 border-red-500/60 shadow-xl transition-all transform hover:-translate-y-1"
            >
              {/* Truck grill-inspired horizontal accent lines */}
              <div className="absolute right-0 top-0 bottom-0 w-24 opacity-10 flex flex-col justify-around py-2 pointer-events-none">
                <div className="h-1 bg-white rounded" />
                <div className="h-1 bg-white rounded" />
                <div className="h-1 bg-white rounded" />
                <div className="h-1 bg-white rounded" />
              </div>
              <div className="flex items-center gap-4 relative z-10">
                <div className="w-14 h-14 rounded-md bg-black/30 border border-white/20 flex items-center justify-center text-white shrink-0 group-hover:scale-110 transition-transform">
                  <i className="fa-solid fa-phone-volume text-2xl text-white" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-wider font-bold text-red-100">
                    Vagtcentral 24/7
                  </div>
                  <div className="font-condensed text-2xl lg:text-3xl font-extrabold uppercase tracking-tight whitespace-nowrap">
                    +45 70 700 800
                  </div>
                  <div className="text-xs text-red-100 mt-0.5 font-medium">
                    Tryk for at ringe direkte
                  </div>
                </div>
              </div>
            </a>

            {/* CTA 2: E-mail */}
            <a
              href="mailto:kontor@mcautotransport.dk"
              className="group relative overflow-hidden bg-zinc-800 hover:bg-zinc-750 text-white p-5 rounded-lg border-2 border-zinc-700 shadow-xl transition-all transform hover:-translate-y-1"
            >
              <div className="absolute right-0 top-0 bottom-0 w-24 opacity-5 flex flex-col justify-around py-2 pointer-events-none">
                <div className="h-1 bg-white rounded" />
                <div className="h-1 bg-white rounded" />
                <div className="h-1 bg-white rounded" />
                <div className="h-1 bg-white rounded" />
              </div>
              <div className="flex items-center gap-4 relative z-10">
                <div className="w-14 h-14 rounded-md bg-zinc-900 border border-zinc-600 flex items-center justify-center text-white shrink-0 group-hover:scale-110 transition-transform">
                  <i className="fa-solid fa-envelope-open-text text-2xl text-red-500" aria-hidden="true" />
                </div>
                <div className="truncate min-w-0">
                  <div className="text-[11px] uppercase tracking-wider font-bold text-zinc-400">
                    Send os en mail
                  </div>
                  <div className="font-condensed text-xl lg:text-2xl font-bold tracking-tight text-white truncate">
                    kontor@mcautotransport.dk
                  </div>
                  <div className="text-xs text-zinc-400 mt-0.5">
                    Vi svarer hurtigt på forespørgsler
                  </div>
                </div>
              </div>
            </a>

            {/* CTA 3: Facebook */}
            <a
              href="https://www.facebook.com/MCautotransport/"
              target="_blank"
              rel="noopener noreferrer"
              className="group relative overflow-hidden bg-[#1877f2] hover:bg-[#166fe5] text-white p-5 rounded-lg border-2 border-blue-400/40 shadow-xl transition-all transform hover:-translate-y-1"
            >
              <div className="absolute right-0 top-0 bottom-0 w-24 opacity-10 flex flex-col justify-around py-2 pointer-events-none">
                <div className="h-1 bg-white rounded" />
                <div className="h-1 bg-white rounded" />
                <div className="h-1 bg-white rounded" />
                <div className="h-1 bg-white rounded" />
              </div>
              <div className="flex items-center gap-4 relative z-10">
                <div className="w-14 h-14 rounded-md bg-black/20 border border-white/20 flex items-center justify-center text-white shrink-0 group-hover:scale-110 transition-transform">
                  <i className="fa-brands fa-facebook text-3xl text-white" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <div className="text-[11px] uppercase tracking-wider font-bold text-blue-100">
                    Følg hverdagen
                  </div>
                  <div className="font-condensed text-2xl lg:text-3xl font-extrabold tracking-tight">
                    Facebook
                  </div>
                  <div className="text-xs text-blue-100 mt-0.5 font-medium">
                    2.200+ følgere · Se fotos og videoer
                  </div>
                </div>
              </div>
            </a>
          </div>
        </div>
      </section>


      {/* 4. LANDSDÆKKENDE SERVICE SEKTION */}
      <section className="py-16 lg:py-24 bg-white border-y border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Left text */}
            <div className="lg:col-span-7 space-y-6">
              <span className="text-sm font-bold uppercase tracking-wider text-red-600 font-condensed">
                Dækker Hele Landet 24/7
              </span>
              <h2 className="font-condensed text-3xl sm:text-5xl font-extrabold uppercase tracking-tight text-zinc-950 leading-tight">
                Landsdækkende autohjælp og transport
              </h2>
              <p className="text-base sm:text-lg text-zinc-700 leading-relaxed">
                MC Transport er ikke kun en lokal aktør. Vi kører i hele Danmark hver eneste dag og servicerer private, erhvervsvirksomheder, forsikringsselskaber og autoværksteder på tværs af Sjælland, Fyn, Jylland samt i resten af Europa.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                <div className="p-4 bg-zinc-50 rounded-lg border border-zinc-200">
                  <div className="flex items-center gap-2 font-condensed text-lg font-bold uppercase text-zinc-900 mb-1">
                    <i className="fa-solid fa-map-location-dot text-red-600" aria-hidden="true" />
                    <span>Afdeling Ugerløse</span>
                  </div>
                  <p className="text-xs text-zinc-600">
                    Annexgårdsvej 7, 4350 Ugerløse. 80.000 m² sikret udendørs plads og 6.000 m² haller.
                  </p>
                </div>

                <div className="p-4 bg-zinc-50 rounded-lg border border-zinc-200">
                  <div className="flex items-center gap-2 font-condensed text-lg font-bold uppercase text-zinc-900 mb-1">
                    <i className="fa-solid fa-map-location-dot text-red-600" aria-hidden="true" />
                    <span>Afdeling Odense</span>
                  </div>
                  <p className="text-xs text-zinc-600">
                    Energivej 36, 5260 Odense. Centralt placeret ved E20 til hurtig indsats på Fyn og øerne.
                  </p>
                </div>

                <div className="p-4 bg-zinc-50 rounded-lg border border-zinc-200">
                  <div className="flex items-center gap-2 font-condensed text-lg font-bold uppercase text-zinc-900 mb-1">
                    <i className="fa-solid fa-map-location-dot text-red-600" aria-hidden="true" />
                    <span>Afdeling Hedensted</span>
                  </div>
                  <p className="text-xs text-zinc-600">
                    Lundagervej 4, 8722 Hedensted. Strategisk placeret for hurtig indsats i Trekantområdet og Jylland.
                  </p>
                </div>
              </div>

              <ul className="space-y-2 text-sm text-zinc-700 pt-2 font-medium">
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-red-600" aria-hidden="true" />
                  <span>Døgnåben vagtcentral – vi tager telefonen når uheldet er ude</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-red-600" aria-hidden="true" />
                  <span>Moderne rød vognpark med skydeladsbiler, kranbiler og sengetrailer</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="fa-solid fa-check text-red-600" aria-hidden="true" />
                  <span>Både akutte udrykninger og faste transportaftaler</span>
                </li>
              </ul>

              <div className="pt-4 flex flex-wrap items-center gap-4">
                <a
                  href="tel:+4570700800"
                  className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-condensed text-xl font-bold uppercase rounded shadow"
                >
                  Ring +45 70 700 800
                </a>
                <Link
                  to="/kontakt"
                  className="px-6 py-3 bg-zinc-100 hover:bg-zinc-200 text-zinc-900 font-condensed text-xl font-bold uppercase rounded"
                >
                  Kontakt os for tilbud
                </Link>
              </div>
            </div>

            {/* Right visual card */}
            <div className="lg:col-span-5 relative">
              <div className="relative rounded-xl overflow-hidden shadow-2xl border-4 border-zinc-950 bg-zinc-900">
                <img
                  src={imageUrl('/_assets/bjaergningsvogn-blokvogn-mc-transport.jpg', 'width=800,quality=85,format=auto')}
                  alt="MC Transport rød bjærgningsvogn med blokvogn"
                  className="w-full h-auto object-cover"
                />
                <div className="p-6 bg-zinc-950 text-white border-t border-zinc-800">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-condensed text-lg font-bold uppercase tracking-wider text-red-500">
                      24 timers døgnvagt
                    </span>
                    <span className="text-xs text-zinc-400">Hele Danmark</span>
                  </div>
                  <p className="text-xs text-zinc-300">
                    Vi transporterer dit køretøj sikkert og effektivt uanset tid og sted. Ring til vores vagttelefon på 70 700 800.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. OM MC TRANSPORT & MARTIN (Personlig præsentation) */}
      <section className="py-16 lg:py-24 bg-zinc-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            {/* Authentisk billede af Martin */}
            <div className="lg:col-span-5 order-2 lg:order-1">
              <div className="relative">
                <div className="absolute -inset-4 bg-red-600/10 rounded-2xl transform -rotate-1 pointer-events-none" />
                <div className="relative rounded-xl overflow-hidden shadow-2xl border border-zinc-300 bg-white">
                  <img
                    src={imageUrl('https://assets.auxodev.dk/assets/7ab2fd9d-1ad5-42a7-a15d-22229bea7818/martin-indehaver-mc-transport-kontor.jpg', 'width=800,quality=90,format=auto')}
                    alt="Martin fra MC Transport på kontoret"
                    className="w-full h-auto object-cover object-top"
                  />
                  <div className="p-5 bg-zinc-900 text-white">
                    <p className="font-condensed text-xl font-bold uppercase text-white">
                      Martin · Indehaver af MC Transport
                    </p>
                    <p className="text-xs text-red-400 font-medium">
                      Familiedrevet vognmandsforretning i Ugerløse
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Tekst om virksomheden & værdier */}
            <div className="lg:col-span-7 order-1 lg:order-2 space-y-5">
              <span className="text-sm font-bold uppercase tracking-wider text-red-600 font-condensed">
                Menneskene Bag MC Transport
              </span>
              <h2 className="font-condensed text-3xl sm:text-5xl font-extrabold uppercase tracking-tight text-zinc-950 leading-tight">
                Vi er din pålidelige partner – bygget på ordentlighed
              </h2>
              <div className="prose prose-zinc text-sm sm:text-base text-zinc-700 space-y-4">
                <p>
                  MC Transport er din pålidelige partner inden for en bred vifte af transportopgaver. Vi specialiserer os i autotransport, godstransport og kørsel med trailer, men vi håndterer også mange andre transportopgaver med lastbil.
                </p>
                <p>
                  Vi er en familiedrevet virksomhed beliggende i Ugerløse ved Holbæk samt med afdeling i Hedensted. Martin er chefen, Pia sidder med regnskab og lønninger, og der er 3 børn som er at finde på kontoret i tide og utide til stor glæde for vores kontoransatte, disponent og chauffører.
                </p>
                <p>
                  Vores erfarne chauffører og moderne udstyr garanterer, at dine transportbehov bliver opfyldt til fuld tilfredshed. Vi forstår vigtigheden af at levere til tiden og uden skader, derfor anvender vi den nyeste teknologi og udstyr til at sikre, at din last bliver behandlet med den største omhu.
                </p>
              </div>

              <div className="pt-2 flex flex-wrap items-center gap-4">
                <Link
                  to="/om-os"
                  className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-condensed text-xl font-bold uppercase rounded shadow"
                >
                  Læs mere om os
                </Link>
                <Link
                  to="/chauffoer-soeges"
                  className="inline-flex items-center text-red-600 hover:text-red-700 font-bold font-condensed uppercase tracking-wide group"
                >
                  Vil du køre for os? Se ledige job →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. SOCIAL PROOF / FACEBOOK AKTIVITET (iPhone 16 Showcase) */}
      <section className="relative w-full max-w-full overflow-hidden bg-slate-100 border-y border-slate-200">
        {/* Background Image with Wash according to Design Spec - fixed on scroll */}
        <div
          className="absolute inset-0 bg-cover bg-center bg-fixed opacity-25 pointer-events-none"
          style={{
            backgroundImage: `url('/_assets/annexgaardsvej-7-luftfoto-1-305.jpg')`,
            backgroundAttachment: 'fixed',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/90 to-white/70 pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 items-center gap-12 lg:gap-14">
            {/* Left Content (cols 12 -> lg:7) */}
            <div className="lg:col-span-7 flex flex-col items-start">
              <span className="text-xs sm:text-sm font-bold uppercase tracking-wider text-red-600 font-condensed">
                MC Transport · Fællesskab & Hverdag
              </span>

              <h2 className="text-4xl sm:text-5xl font-extrabold tracking-tight text-neutral-950 leading-[1.15] mt-2 mb-4 font-condensed uppercase">
                Følg MC Transport på Facebook
              </h2>

              <p className="text-base sm:text-lg font-normal text-slate-800 leading-relaxed max-w-xl mb-6">
                Over 2.200 følgere følger med i vores hverdag, opgaver og nyheder. Følg os for de nyeste billeder og videoer af vores flotte røde vognpark i aktion på landevejene, akutte bjærgninger og spændende maskintransporter.
              </p>

              {/* Highlights */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 mb-8 w-full max-w-lg">
                <div className="flex items-center gap-3 bg-white/80 backdrop-blur-sm px-4 py-2.5 rounded-lg border border-slate-200/80 shadow-xs">
                  <div className="w-8 h-8 rounded-full bg-blue-50 text-[#1877f2] flex items-center justify-center shrink-0">
                    <i className="fa-brands fa-facebook-f text-sm" aria-hidden="true" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-neutral-900">2.200+ Følgere</div>
                    <div className="text-[11px] text-slate-500">Aktivt lokalt fællesskab</div>
                  </div>
                </div>

                <div className="flex items-center gap-3 bg-white/80 backdrop-blur-sm px-4 py-2.5 rounded-lg border border-slate-200/80 shadow-xs">
                  <div className="w-8 h-8 rounded-full bg-red-50 text-red-600 flex items-center justify-center shrink-0">
                    <i className="fa-solid fa-camera text-xs" aria-hidden="true" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-neutral-900">Direkte fra vejen</div>
                    <div className="text-[11px] text-slate-500">Billeder af bjærgning & kørsel</div>
                  </div>
                </div>
              </div>

              {/* Pill CTA button per design specification */}
              <a
                href="https://www.facebook.com/MCautotransport/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2.5 px-7 py-3.5 rounded-full bg-[#6c7d93] hover:bg-[#5a6b80] text-white text-sm font-semibold shadow-sm transition-all duration-150 ease-in-out hover:shadow-md hover:-translate-y-0.5 cursor-pointer"
              >
                <i className="fa-brands fa-facebook-f text-sm" aria-hidden="true" />
                <span>Besøg os på Facebook</span>
              </a>
            </div>

            {/* Right Device Showcase (iPhone 16 Frame) (cols 12 -> lg:5) */}
            <div className="lg:col-span-5 flex justify-center lg:justify-end overflow-hidden py-3 px-1">
              <div className="relative w-full max-w-[290px] xs:max-w-[325px] sm:max-w-[360px]">
                {/* Physical side buttons on iPhone */}
                <div className="absolute -left-[3px] top-24 w-[3px] h-7 bg-neutral-800 rounded-l-xs" />
                <div className="absolute -left-[3px] top-36 w-[3px] h-11 bg-neutral-800 rounded-l-xs" />
                <div className="absolute -left-[3px] top-51 w-[3px] h-11 bg-neutral-800 rounded-l-xs" />
                <div className="absolute -right-[3px] top-32 w-[3px] h-14 bg-neutral-800 rounded-r-xs" />

                {/* iPhone Chassis with smooth float animation */}
                <div className="relative w-full rounded-[3.25rem] p-3 sm:p-3.5 bg-neutral-950 ring-1 ring-slate-400/40 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.35)] select-none animate-float-gentle">
                  {/* Screen Canvas (40px higher: 660px) */}
                  <div className="relative w-full h-[660px] bg-white rounded-[2.5rem] overflow-hidden flex flex-col border border-slate-200/80 shadow-inner">
                    {/* Status Bar with Dynamic Island */}
                    <div className="w-full pt-2.5 pb-1 px-6 flex justify-between items-center text-xs font-semibold text-neutral-900 z-20 bg-white">
                      <span className="w-10 text-[11px] font-bold">9:41</span>
                      {/* Dynamic Island */}
                      <div className="h-5 w-28 bg-black rounded-full mx-auto flex items-center justify-end px-2 gap-1.5 shadow-xs">
                        <div className="w-2 h-2 rounded-full bg-neutral-900 border border-neutral-800" />
                        <div className="w-2.5 h-2.5 rounded-full bg-neutral-950 ring-1 ring-neutral-800/80" />
                      </div>
                      <div className="w-10 flex items-center justify-end gap-1.5 text-[10px] text-neutral-800">
                        <i className="fa-solid fa-signal" aria-hidden="true" />
                        <span className="text-[9px] font-bold">5G</span>
                        <i className="fa-solid fa-battery-full text-xs" aria-hidden="true" />
                      </div>
                    </div>

                    {/* Facebook App Header */}
                    <div className="border-b border-slate-200 px-4 py-2 flex items-center justify-between bg-white shrink-0">
                      <span className="text-[#1877f2] font-black text-xl tracking-tighter">facebook</span>
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-slate-700 text-xs">
                          <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
                        </div>
                        <div className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-slate-700 text-xs">
                          <i className="fa-brands fa-facebook-messenger" aria-hidden="true" />
                        </div>
                      </div>
                    </div>

                    {/* App Tabs Bar */}
                    <div className="grid grid-cols-5 border-b border-slate-200 bg-white text-slate-500 text-sm py-1.5 text-center shrink-0">
                      <div className="text-[#1877f2] relative flex justify-center py-1">
                        <i className="fa-solid fa-house" aria-hidden="true" />
                        <div className="absolute bottom-[-6px] inset-x-3 h-0.5 bg-[#1877f2] rounded-full" />
                      </div>
                      <div className="flex justify-center py-1"><i className="fa-solid fa-tv" aria-hidden="true" /></div>
                      <div className="flex justify-center py-1"><i className="fa-solid fa-store" aria-hidden="true" /></div>
                      <div className="flex justify-center py-1"><i className="fa-solid fa-bell" aria-hidden="true" /></div>
                      <div className="flex justify-center py-1"><i className="fa-solid fa-bars" aria-hidden="true" /></div>
                    </div>

                    {/* Feed Content Area */}
                    <div className="flex-1 overflow-y-auto bg-slate-100 divide-y divide-slate-200">
                      {/* Facebook Post Card */}
                      <div className="bg-white pb-3">
                        {/* Post Header */}
                        <div className="p-3 flex items-center justify-between">
                          <div className="flex items-center gap-2.5">
                            <div className="w-9 h-9 rounded-full ring-2 ring-blue-500/20 bg-white p-0.5 overflow-hidden shrink-0 flex items-center justify-center shadow-xs">
                              <img src="/_assets/mc-transport-logo.png" alt="MC Autotransport" className="w-full h-full object-contain" />
                            </div>
                            <div>
                              <div className="flex items-center gap-1">
                                <span className="font-bold text-neutral-950 text-xs leading-none">MC Autotransport</span>
                                <span className="w-3.5 h-3.5 rounded-full bg-[#1877f2] text-white flex items-center justify-center text-[8px]">
                                  <i className="fa-solid fa-check" aria-hidden="true" />
                                </span>
                              </div>
                              <div className="flex items-center gap-1 text-[10px] text-slate-500 mt-0.5">
                                <span>1 t.</span>
                                <span>·</span>
                                <i className="fa-solid fa-earth-americas text-[9px]" aria-hidden="true" />
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2">
                            <a
                              href="https://www.facebook.com/MCautotransport/"
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-2.5 py-1 bg-[#1877f2] hover:bg-[#166fe5] text-white text-[11px] font-semibold rounded-full flex items-center gap-1 shadow-xs"
                            >
                              <i className="fa-solid fa-plus text-[9px]" aria-hidden="true" />
                              <span>Følg side</span>
                            </a>
                            <i className="fa-solid fa-ellipsis text-slate-400 text-xs px-1" aria-hidden="true" />
                          </div>
                        </div>

                        {/* Post Text */}
                        <div className="px-3 pb-2.5 text-xs text-slate-800 leading-snug">
                          Selv om det er lidt vådt i vejret i tiden, holder vi stadig Danmark i gang! 😃🚛 Her er lidt blandet af hvad vi får tiden til at gå med. Ring døgnet rundt på <span className="text-[#1877f2] font-semibold">70 700 800</span> hvis uheldet er ude! 🇩🇰
                        </div>

                        {/* 2x2 Photo Grid with +9 Overlay */}
                        <div className="grid grid-cols-2 gap-0.5 bg-slate-200">
                          <div className="relative aspect-4/3 overflow-hidden bg-slate-100">
                            <img
                              src={imageUrl('/_assets/mc-autotransporter-med-skadede-biler.png', 'width=300,height=225,fit=cover,quality=80,format=auto')}
                              alt="Rød autotransporter med biler"
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="relative aspect-4/3 overflow-hidden bg-slate-100">
                            <img
                              src={imageUrl('/_assets/volvo-fejeblad-det-roede-lyn.jpg', 'width=300,height=225,fit=cover,quality=80,format=auto')}
                              alt="Rød Volvo fejeblad Det Røde Lyn"
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="relative aspect-4/3 overflow-hidden bg-slate-100">
                            <img
                              src={imageUrl('/_assets/scania-lastbil-blokvogn-transport.jpg', 'width=300,height=225,fit=cover,quality=80,format=auto')}
                              alt="Rød Scania med blokvogn og chauffør"
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="relative aspect-4/3 overflow-hidden bg-slate-100 group">
                            <img
                              src={imageUrl('/_assets/bjaergningsvogn-blokvogn-mc-transport.jpg', 'width=300,height=225,fit=cover,quality=80,format=auto')}
                              alt="Rød bjærgningsvogn på vej"
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-neutral-950/60 backdrop-blur-[1px] flex items-center justify-center text-white font-extrabold text-xl tracking-wider">
                              +9
                            </div>
                          </div>
                        </div>

                        {/* Engagement Stats */}
                        <div className="px-3 py-2 flex items-center justify-between text-[11px] text-slate-500 border-b border-slate-100">
                          <div className="flex items-center gap-1.5">
                            <div className="flex -space-x-1">
                              <span className="w-4 h-4 rounded-full bg-[#1877f2] text-white flex items-center justify-center text-[8px] ring-1 ring-white">
                                <i className="fa-solid fa-thumbs-up" aria-hidden="true" />
                              </span>
                              <span className="w-4 h-4 rounded-full bg-red-500 text-white flex items-center justify-center text-[8px] ring-1 ring-white">
                                <i className="fa-solid fa-heart" aria-hidden="true" />
                              </span>
                            </div>
                            <span className="font-medium text-slate-600">148</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span>24 kommentarer</span>
                            <span>·</span>
                            <span>9 delinger</span>
                          </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="grid grid-cols-3 pt-1 px-1 text-slate-600 text-xs font-semibold text-center">
                          <div className="flex items-center justify-center gap-1.5 py-1.5 hover:bg-slate-50 rounded-md">
                            <i className="fa-regular fa-thumbs-up text-sm" aria-hidden="true" />
                            <span>Synes godt om</span>
                          </div>
                          <div className="flex items-center justify-center gap-1.5 py-1.5 hover:bg-slate-50 rounded-md">
                            <i className="fa-regular fa-comment text-sm" aria-hidden="true" />
                            <span>Kommentér</span>
                          </div>
                          <div className="flex items-center justify-center gap-1.5 py-1.5 hover:bg-slate-50 rounded-md">
                            <i className="fa-solid fa-share text-sm" aria-hidden="true" />
                            <span>Del</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* iPhone Home Indicator bar */}
                    <div className="w-full py-1.5 bg-white flex justify-center shrink-0">
                      <div className="w-28 h-1 bg-neutral-900 rounded-full" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7. GALLERY SLIDER (Design reference "Gallery" #54a41ca3-1f3f-4869-9a8c-67bf62680e4b) */}
      <section className="bg-white py-16 lg:py-20 border-b border-zinc-200 w-full max-w-full overflow-hidden">
        {/* Top Area: Horizontally centered text block */}
        <div className="max-w-2xl mx-auto text-center px-4 mb-10 lg:mb-12">
          <h2 className="font-condensed text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase text-neutral-900 tracking-tight">
            Glimt fra hverdagen hos MC Transport
          </h2>
          <p className="mt-3 text-base sm:text-lg text-neutral-600 font-normal leading-relaxed">
            Se vores moderne røde vognpark i aktion med autotransport, tung bjærgning og specialkørsler overalt på de danske veje.
          </p>
        </div>

        {/* Bottom Area: Full-bleed horizontal image slider/carousel with sharp corners */}
        <div
          className="relative w-full max-w-full overflow-hidden select-none"
          onMouseEnter={() => setIsGalleryPaused(true)}
          onMouseLeave={() => setIsGalleryPaused(false)}
        >
          {/* Slider track */}
          <div
            className="flex gap-3 sm:gap-4 transition-transform duration-700 ease-out will-change-transform"
            style={{
              transform: `translateX(-${(galleryIndex % GALLERY_IMAGES.length) * 20}rem)`
            }}
          >
            {/* Duplicated for seamless loop presentation */}
            {[...GALLERY_IMAGES, ...GALLERY_IMAGES].map((img, idx) => (
              <div
                key={idx}
                className="w-72 sm:w-80 lg:w-[20rem] shrink-0 aspect-3/4 relative overflow-hidden bg-neutral-950 group"
              >
                <img
                  src={imageUrl(img.src, 'width=450,height=300,fit=cover,quality=80,format=auto')}
                  alt={`${img.title} - MC Transport vognpark`}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                  <p className="text-white text-sm font-semibold tracking-wide">{img.title}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Left Navigation Overlay Button */}
          <button
            type="button"
            onClick={handlePrevGallery}
            aria-label="Forrige billede"
            className="absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-black/65 hover:bg-black/85 text-white flex items-center justify-center transition-all cursor-pointer shadow-lg backdrop-blur-xs active:scale-95"
          >
            <i className="fa-solid fa-chevron-left text-base sm:text-lg" aria-hidden="true" />
          </button>

          {/* Right Navigation Overlay Button */}
          <button
            type="button"
            onClick={handleNextGallery}
            aria-label="Næste billede"
            className="absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 z-20 w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-black/65 hover:bg-black/85 text-white flex items-center justify-center transition-all cursor-pointer shadow-lg backdrop-blur-xs active:scale-95"
          >
            <i className="fa-solid fa-chevron-right text-base sm:text-lg" aria-hidden="true" />
          </button>
        </div>
      </section>

      {/* 8. ALORA DESIGN REFERENCE (#4a403c30-405f-4282-9c35-7bcd636d9826) & FAQ */}
      <section className="relative py-16 sm:py-20 lg:py-28 bg-neutral-950 overflow-hidden w-full max-w-full">
        {/* Background image with solid dark color overlay */}
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30 pointer-events-none"
          style={{ backgroundImage: `url('/_assets/annexgaardsvej-7-luftfoto-2-306.jpg')` }}
        />
        <div className="absolute inset-0 bg-neutral-950/85 pointer-events-none" />

        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Elevated Surface Card with generous rounded corners */}
          <div className="bg-white rounded-2xl sm:rounded-3xl p-5 sm:p-10 lg:p-16 shadow-2xl border border-zinc-100">
            {/* Header intro from JSON */}
            <div className="max-w-4xl mb-10 sm:mb-14 pb-8 border-b border-zinc-200">
              <span className="inline-block px-3 py-1 bg-red-50 text-red-600 rounded text-xs font-bold uppercase tracking-wider font-condensed mb-3">
                Kerneopgave · Hele Danmark · MC Transport
              </span>
              <h1 className="font-condensed text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase text-neutral-900 tracking-tight leading-[1.1] mb-4">
                Autotransport og autohjælp i hele Danmark
              </h1>
              <p className="text-zinc-700 text-base sm:text-lg lg:text-xl leading-relaxed font-normal">
                Hos MC Transport er autotransport vores centrale opgave. Vi flytter nye, brugte, skadede og brændte biler for både private og virksomheder. Uanset om du skal have én bil eller flere køretøjer flyttet, løser vi autotransport i hele Danmark med materiel, der vælges efter opgaven.
              </p>
            </div>

            {/* 4 sequential content cards in 2-column grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-stretch">
              {/* Card 1: Har du brug for autotransport af én bil eller flere */}
              <div className="bg-zinc-50 p-6 sm:p-8 rounded-2xl border border-zinc-200/80 shadow-sm flex flex-col justify-between">
                <div className="space-y-4">
                  <div className="flex items-center gap-2.5">
                    <span className="w-2.5 h-6 bg-red-600 rounded-sm inline-block shrink-0" />
                    <h2 className="font-condensed text-2xl sm:text-3xl font-extrabold uppercase text-neutral-900 tracking-tight">
                      Har du brug for autotransport af én bil eller flere
                    </h2>
                  </div>
                  <p className="text-zinc-700 text-base leading-relaxed">
                    Vi råder over skydeladsbiler, kranbiler og transportere med plads til flere biler. Det giver os mulighed for at håndtere både køreklare biler og køretøjer, som kræver særlig læsning.{' '}
                    <Link to="/vi-tilbyder/autotransport.aspx" className="text-red-600 font-bold hover:underline">
                      Læs mere om vores autotransport og det materiel, vi bruger til forskellige transportopgaver
                    </Link>.
                  </p>
                  <p className="text-zinc-700 text-base leading-relaxed">
                    Prisen på autotransport afhænger blandt andet af afstand, antal køretøjer, bilens stand og behovet for kran eller særlig læsning. Vi beregner derfor din autotransport pris ud fra den konkrete opgave. Du kan kontakte os og få et tilbud, før transporten planlægges.
                  </p>
                </div>
                <div className="pt-6 mt-4 border-t border-zinc-200 flex items-center justify-between">
                  <Link
                    to="/kontakt"
                    className="inline-flex items-center gap-2 text-sm font-bold uppercase font-condensed tracking-wider text-red-600 hover:text-red-700 transition-colors"
                  >
                    <span>Få et uforpligtende tilbud</span>
                    <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                  </Link>
                  <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider font-condensed">
                    1 eller flere biler
                  </span>
                </div>
              </div>

              {/* Card 2: Autohjælp når bilen ikke kan køre videre */}
              <div className="bg-zinc-50 p-6 sm:p-8 rounded-2xl border border-zinc-200/80 shadow-sm flex flex-col justify-between">
                <div className="space-y-4">
                  <div className="flex items-center gap-2.5">
                    <span className="w-2.5 h-6 bg-red-600 rounded-sm inline-block shrink-0" />
                    <h2 className="font-condensed text-2xl sm:text-3xl font-extrabold uppercase text-neutral-900 tracking-tight">
                      Autohjælp når bilen ikke kan køre videre
                    </h2>
                  </div>
                  <p className="text-zinc-700 text-base leading-relaxed">
                    Når et køretøj ikke kan fortsætte, har du brug for en løsning, der passer til situationen. Vores vagtcentral er åben døgnet rundt og tager imod akutte henvendelser om autohjælp. Her kan du få afklaret, om behovet er vejhjælp, bugsering, bjærgning eller fritrækning.
                  </p>
                  <div className="pt-4 border-t border-zinc-200 space-y-2.5">
                    <h3 className="font-condensed text-xl sm:text-2xl font-bold uppercase text-zinc-900">
                      Hvornår er bugsering eller bjærgning den rigtige løsning
                    </h3>
                    <p className="text-zinc-700 text-base leading-relaxed">
                      Bugsering bruges typisk, når bilen skal flyttes til et værksted eller en anden adresse. Er bilen fastkørt eller svært tilgængelig, kan bjærgning eller fritrækning være nødvendig. Vi vurderer opgaven og vælger et egnet bjærgningskøretøj til situationen.
                    </p>
                  </div>
                </div>
                <div className="pt-6 mt-4 border-t border-zinc-200 flex flex-wrap gap-2 sm:gap-3">
                  <Link
                    to="/autohjaelp"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-zinc-300 rounded text-xs font-bold uppercase font-condensed hover:border-red-600 hover:text-red-600 transition-colors"
                  >
                    <i className="fa-solid fa-truck-medical text-red-600" aria-hidden="true" />
                    <span>Autohjælp 24/7</span>
                  </Link>
                  <Link
                    to="/bugsering-af-bil"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-zinc-300 rounded text-xs font-bold uppercase font-condensed hover:border-red-600 hover:text-red-600 transition-colors"
                  >
                    <i className="fa-solid fa-truck-pickup text-red-600" aria-hidden="true" />
                    <span>Bugsering</span>
                  </Link>
                  <Link
                    to="/bjaergning"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-zinc-300 rounded text-xs font-bold uppercase font-condensed hover:border-red-600 hover:text-red-600 transition-colors"
                  >
                    <i className="fa-solid fa-truck-ramp-box text-red-600" aria-hidden="true" />
                    <span>Bjærgning</span>
                  </Link>
                  <Link
                    to="/fritraekning"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-zinc-300 rounded text-xs font-bold uppercase font-condensed hover:border-red-600 hover:text-red-600 transition-colors"
                  >
                    <i className="fa-solid fa-link text-red-600" aria-hidden="true" />
                    <span>Fritrækning</span>
                  </Link>
                </div>
              </div>

              {/* Card 3: Når opgaven kræver mere end almindelig autotransport */}
              <div className="bg-zinc-50 p-6 sm:p-8 rounded-2xl border border-zinc-200/80 shadow-sm flex flex-col justify-between">
                <div className="space-y-4">
                  <div className="flex items-center gap-2.5">
                    <span className="w-2.5 h-6 bg-red-600 rounded-sm inline-block shrink-0" />
                    <h2 className="font-condensed text-2xl sm:text-3xl font-extrabold uppercase text-neutral-900 tracking-tight">
                      Når opgaven kræver mere end almindelig autotransport
                    </h2>
                  </div>
                  <p className="text-zinc-700 text-base leading-relaxed">
                    MC Transport har autotransport som kerneområde og løser også maskintransport, krantransport og godstransport. Vi kører blandt andet med entreprenørmateriel, containere og både. Opgaven kan udføres med sengetrailer eller gardintrailer med medbringertruck, når transporten og forholdene ved aflæsning kræver det.
                  </p>
                  <p className="text-zinc-700 text-base leading-relaxed">
                    Har du brug for at samle transport og midlertidig opbevaring, tilbyder vi også opbevaring af køretøjer og gods. Hos MC Transport ser vi på hele opgaven, så valg af køretøj, læsning og levering hænger sammen.{' '}
                    <Link to="/om-os" className="text-red-600 font-bold hover:underline">
                      Læs mere om os og vores materiel
                    </Link>.
                  </p>
                </div>
                <div className="pt-6 mt-4 border-t border-zinc-200 flex flex-wrap gap-2 text-xs font-bold uppercase font-condensed text-zinc-600">
                  <span className="px-2.5 py-1 bg-white border border-zinc-200 rounded">Maskintransport</span>
                  <span className="px-2.5 py-1 bg-white border border-zinc-200 rounded">Krantransport</span>
                  <span className="px-2.5 py-1 bg-white border border-zinc-200 rounded">Sengetrailer</span>
                  <span className="px-2.5 py-1 bg-white border border-zinc-200 rounded">30.000 m² Plads</span>
                </div>
              </div>

              {/* Card 4: Lad os planlægge den rigtige transport (Action / Vagtcentral Card) */}
              <div className="bg-zinc-50 p-6 sm:p-8 rounded-2xl border border-zinc-200/80 shadow-sm flex flex-col justify-between">
                <div className="space-y-4">
                  <div className="flex items-center gap-2.5">
                    <span className="w-2.5 h-6 bg-red-600 rounded-sm inline-block shrink-0" />
                    <h2 className="font-condensed text-2xl sm:text-3xl font-extrabold uppercase text-neutral-900 tracking-tight">
                      Lad os planlægge den rigtige transport
                    </h2>
                  </div>
                  <p className="text-zinc-700 text-base leading-relaxed">
                    Fortæl os, hvad der skal flyttes, hvor det skal hentes og leveres, og om køretøjet kan køre. De oplysninger gør det lettere at vælge materiel og planlægge opgaven. Kontakt MC Transport for autotransport i hele Danmark eller akut hjælp via vores vagtcentral døgnet rundt.
                  </p>
                </div>
                <div className="pt-6 mt-6 border-t border-zinc-200 space-y-3">
                  <a
                    href="tel:+4570700800"
                    className="w-full inline-flex items-center justify-center gap-3 px-5 py-3.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold font-condensed uppercase tracking-wider text-base transition-colors shadow-md"
                  >
                    <i className="fa-solid fa-phone text-sm" aria-hidden="true" />
                    <span>Ring 70 700 800 (Døgnvagt)</span>
                  </a>
                  <Link
                    to="/kontakt"
                    className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-white hover:bg-zinc-100 text-neutral-900 rounded-xl font-bold font-condensed uppercase tracking-wider text-sm transition-colors border border-zinc-300"
                  >
                    <span>Send forespørgsel / få tilbud</span>
                    <i className="fa-solid fa-arrow-right text-xs text-red-600" aria-hidden="true" />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 9. FAQ SEKTION */}
      <section className="py-16 sm:py-20 lg:py-28 bg-neutral-950 text-white relative w-full max-w-full overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 xl:gap-16 items-stretch">
            {/* Venstre kolonne: large flush-edged image */}
            <div className="lg:col-span-5 flex">
              <div className="w-full h-[360px] sm:h-[460px] lg:h-auto min-h-full rounded-2xl overflow-hidden relative shadow-2xl">
                <img
                  src={imageUrl('/_assets/bjaergningsvogn-autohjaelp-havareret-lastbil.jpg', 'width=1000,quality=85,format=auto')}
                  alt="MC Transport autohjælp og bjærgning i aktion"
                  className="w-full h-full object-cover object-center"
                  loading="lazy"
                />
              </div>
            </div>

            {/* Højre kolonne: FAQ accordion list */}
            <div className="lg:col-span-7 flex flex-col justify-center">
              <div className="mb-6 sm:mb-8">
                <span className="text-xs font-bold uppercase tracking-wider text-red-500 font-condensed">
                  Svar på typiske spørgsmål
                </span>
                <h2 className="font-condensed text-3xl sm:text-4xl lg:text-5xl font-extrabold uppercase tracking-tight text-white mt-2">
                  Ofte stillede spørgsmål om autotransport og autohjælp
                </h2>
                <p className="mt-3 text-zinc-400 font-sans italic text-base sm:text-lg leading-relaxed">
                  Få hurtigt overblik over hvordan vi beregner priser, håndterer akutte bjærgninger og fragter personbiler, maskiner og tungvogne i hele landet.
                </p>
              </div>

              <FaqAccordion items={FAQ_ITEMS} variant="dark" />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
